import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { Modal } from "components/Modal/Modal";
import { PrimaryButton } from "components/Button/PrimaryButton";

interface RegenerateModalProps {
  show: boolean;
  onClose: () => void;
  onConfirm: (regenerationContext: string) => void;
}

// "Regenerate with AI" confirmation (plan §5): optional steering-context
// textarea + the explicit note that a PROPOSAL will be generated for review —
// the current draft stays untouched until every diff section is decided
// (contract endpoint 4, mode "regenerate").
export const RegenerateModal = ({
  show,
  onClose,
  onConfirm,
}: RegenerateModalProps) => {
  const { t } = useTranslation();
  const [context, setContext] = useState("");

  // Steering context is per-regeneration — every open starts blank.
  useEffect(() => {
    if (show) {
      setContext("");
    }
  }, [show]);

  return (
    <Modal
      show={show}
      onClose={onClose}
      width="1/2"
      content={
        <div>
          <h3 className="text-lg font-medium text-gray-900">
            {t("risks.ai.regenerate.headline")}
          </h3>
          <p className="mt-2 text-sm text-gray-500 whitespace-pre-line">
            {t("risks.ai.regenerate.subline")}
          </p>

          <label
            htmlFor="ai-hazard-regenerate-context"
            className="mt-4 block text-sm font-medium text-gray-700"
          >
            {t("risks.ai.regenerate.context.label")}
          </label>
          <textarea
            id="ai-hazard-regenerate-context"
            rows={4}
            value={context}
            onChange={(event) => setContext(event.target.value)}
            placeholder={t("risks.ai.regenerate.context.placeholder")}
            className="mt-1 w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500 sm:text-sm"
          />

          <div className="mt-6 flex justify-end items-center gap-4">
            <button
              type="button"
              onClick={onClose}
              className="text-sm text-gray-500 hover:text-gray-700"
            >
              {t("steps.risks.confirmation.cancel")}
            </button>
            <PrimaryButton
              testId="ai-hazard-regenerate"
              onClick={() => onConfirm(context)}
            >
              {t("risks.ai.regenerate.cta")}
            </PrimaryButton>
          </div>
        </div>
      }
    />
  );
};
